import { useMemo } from 'react';
import { serverOffsetLabel, useServerOffset } from '../shell/serverTime';
import {
  numberFormatOf,
  timeZoneOf,
  usePrefsValue,
  type NumberFormat,
  type TimeZoneMode,
} from './prefsStore';

/** How times are shown: the offset from UTC at an instant (local time moves with daylight saving) and a label. */
export interface DisplayZone {
  mode: TimeZoneMode;
  offsetAt: (ms: number) => number;
  /** "server UTC+3", "local UTC+5:30", "UTC". */
  label: string;
}

export function displayZone(mode: TimeZoneMode, serverOffset: number, now = Date.now()): DisplayZone {
  if (mode === 'utc') return { mode, offsetAt: () => 0, label: 'UTC' };
  if (mode === 'local') {
    const offsetAt = (ms: number) => -new Date(ms).getTimezoneOffset();
    return { mode, offsetAt, label: `local ${serverOffsetLabel(offsetAt(now))}` };
  }
  return { mode, offsetAt: () => serverOffset, label: `server ${serverOffsetLabel(serverOffset)}` };
}

/** The time zone the user chose for the terminal's times (server time unless they changed it). */
export function useDisplayZone(): DisplayZone {
  const mode = usePrefsValue((s) => timeZoneOf(s.data));
  const serverOffset = useServerOffset() ?? 0;
  return useMemo(() => displayZone(mode, serverOffset), [mode, serverOffset]);
}

const LOCALES: Record<NumberFormat, string> = { en: 'en-US', de: 'de-DE', fr: 'fr-FR', plain: 'en-US' };

export const NUMBER_FORMATS: { id: NumberFormat; label: string }[] = [
  { id: 'en', label: '1,234.56' },
  { id: 'de', label: '1.234,56' },
  { id: 'fr', label: '1 234,56' },
  { id: 'plain', label: '1234.56' },
];

/** Formats amounts of money (two decimals unless told otherwise) the way the user chose. Prices keep their own digits. */
export function moneyFormatter(format: NumberFormat, decimals = 2): (value: number) => string {
  const intl = new Intl.NumberFormat(LOCALES[format], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: format !== 'plain',
  });
  // fr-FR groups with a narrow no-break space; a plain no-break space reads the same and copies better.
  return (value: number) => intl.format(value).replace(/\u202f/g, '\u00a0');
}

export function useMoneyFormat(decimals = 2): (value: number) => string {
  const format = usePrefsValue((s) => numberFormatOf(s.data));
  return useMemo(() => moneyFormatter(format, decimals), [format, decimals]);
}
